import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environment/environment';

import {
  HospitalUserAffiliationDTO,
  HospitalUserAffiliationUpdateRequestDTO,
  HospitalUserAffiliationReadByIdRequestDTO
} from 'src/app/hospital/HospitalUserMapping/user-affiliation-mapping.interface';

import { HospitalUserAffiliationService } from
  'src/app/hospital/HospitalUserMapping/user-affiliation-mapping.service';

import { UserLoginService } from 'src/app/login/userlogin.service';
import { UserPermissionService } from
  'src/app/Common/UserPermission/userPermission.service';

import {
  SignatureRequestDTO,
  SignatureResponseDTO
} from 'src/app/Common/UserPermission/userpermission.interface';

import { EncryptionServiceService } from
  'src/app/Common/EncryptionService/encryption-service.service';

interface HospitalUserAffiliationExternalEncryptedRequestDTO {
  EncData: string;
  TokenKey: string;
}

@Component({
  selector: 'app-patient-concent',
  templateUrl: './patient-concent.component.html',
  styleUrls: ['./patient-concent.component.css']
})
export class PatientConcentComponent implements OnInit {
  mappingId: number = 0;

  isLoading: boolean = false;
  isSubmitting: boolean = false;
  isAgreed: boolean = false;
  isSubmitted: boolean = false;
  showFullConsent: boolean = false;

  private User = inject(UserLoginService).User();
  private destroyRef = inject(DestroyRef);

  latitude: number | null = null;
  longitude: number | null = null;
  locationAccuracy: number | null = null;

  patientConsentData: HospitalUserAffiliationDTO | null = null;

  consentText: string = `
Patient Admission, Billing, Data Sharing and Finance Consent

I confirm that I am the patient, authorized relative, guardian or attendant and I am providing this consent voluntarily for hospital admission, billing, payment processing and related services.

I authorize the hospital, its doctors, nurses, medical staff and authorized service providers to admit the patient and provide necessary medical evaluation, monitoring, nursing care, medicines, investigations and hospital services.

I understand that this is a general admission consent. Separate consent may be required for surgery, anesthesia, blood transfusion, ICU procedures, high-risk procedures or any specific medical procedure where separate informed consent is required.

In case of emergency or deterioration of the patient’s condition, I authorize the hospital and medical team to provide necessary emergency care in the best interest of the patient.

I understand that hospital charges may include registration, consultation, room, nursing, doctor visit, procedure, surgery, ICU, investigation, medicine, consumables, equipment, administrative and other applicable charges.

I understand that the hospital may collect an initial deposit at the time of admission and may request additional deposit or payment during treatment depending on actual treatment, room category, procedure, insurance status, loan status or hospital policy.

I agree that I am responsible for clearing the final hospital bill after adjustment of deposit, discount, insurance approval, loan disbursal, refund or any other applicable amount.

I understand that final discharge may be subject to medical clearance, nursing clearance, pharmacy clearance, investigation clearance, insurance/TPA clearance, loan/payment confirmation and final billing clearance.

I authorize the hospital to share relevant patient, admission, billing, payment, KYC and discharge-related information with Nishwik for digital admission support, consent management, payment assistance, EMI/loan assistance, billing support, reconciliation and service communication.

If I opt for EMI, loan or payment finance assistance, I voluntarily authorize Nishwik to process my request and share my details with its authorized lending, NBFC, banking or technology partners for eligibility check, KYC, credit assessment, approval, disbursal, repayment and servicing.

I understand that loan or EMI approval is subject to eligibility, KYC verification, credit assessment, lender policy and documentation. I understand that loan approval is not guaranteed. If the loan is rejected, delayed, cancelled or partially approved, I remain responsible for paying the hospital bill.

I authorize the hospital, Nishwik and their authorized partners to contact me through SMS, WhatsApp, phone call, email or app notification for admission, billing, payment, discharge, consent, KYC, loan/EMI and service-related communication.

I confirm that the details shown to me are correct, the consent has been explained to me in a language I understand, and I have been given an opportunity to ask questions. I am signing this consent voluntarily.
`; 

  constructor(
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private hospitalUserAffiliationService: HospitalUserAffiliationService,
    private userPermissionService: UserPermissionService,
    private encryptionService: EncryptionServiceService
  ) {}

  ngOnInit(): void {
    this.mappingId = Number(this.route.snapshot.paramMap.get('mappingId') || 0);

    if (!this.mappingId || this.mappingId <= 0) {
      this.toastr.error('Invalid consent link.');
      return;
    }

    this.initializePatientConsent();
    this.captureLocation();
  }

  private initializePatientConsent(): void {
    this.loadPatientConsentByMappingId();
  }

  /**
   * READ FLOW:
   * { mappingId } -> encrypt -> generate signature -> ReadById_External -> bind response
   */
  loadPatientConsentByMappingId(): void {
    if (!this.mappingId || this.mappingId <= 0) {
      this.toastr.error('Invalid consent link.');
      return;
    }

    this.isLoading = true;

    try {
      const rawPayload: HospitalUserAffiliationReadByIdRequestDTO = {
        mappingId: Number(this.mappingId)
      };

      const encryptedBody = this.createEncryptedExternalBody(rawPayload);

      const signaturePayload: SignatureRequestDTO = {
        apiKey: environment.hospitalExternalApiKey,
        privateKeyPem: environment.privateKeyPem,
        method: 'POST',
        pathAndQuery: '/ReadById_External',
        body: JSON.stringify(encryptedBody)
      };

      this.userPermissionService.UserPermission_generateSignature(signaturePayload)
        .pipe(
          switchMap((signatureResponse: any) => {
            const signatureData = this.getValidatedSignatureResponse(signatureResponse);

            return this.hospitalUserAffiliationService.HospitalUserAffiliationReadById_External(
              encryptedBody as unknown as HospitalUserAffiliationReadByIdRequestDTO,
              signatureData.xApiKey,
              signatureData.xApiTimestamp,
              signatureData.xApiNonce,
              signatureData.xApiSignature
            );
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (response: any) => {
            this.isLoading = false;
            this.bindPatientConsentData(response);
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Patient consent ReadById External failed:', error);
            const message = error?.error?.message || error?.error?.title || error?.message || 'Failed to load patient consent details.';
            this.toastr.error(message);
          }
        });
    } catch (error: any) {
      this.isLoading = false;
      console.error('Unable to prepare encrypted ReadById request:', error);
      this.toastr.error(error?.message || 'Unable to prepare patient consent request.');
    }
  }

  captureLocation(): void {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.locationAccuracy = position.coords.accuracy;
      },
      () => {
        this.latitude = null;
        this.longitude = null;
        this.locationAccuracy = null;
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  submitConsent(): void {
    if (!this.mappingId || this.mappingId <= 0) {
      this.toastr.error('Invalid consent link.');
      return;
    }

    if (!this.isAgreed) {
      this.toastr.error('Please accept the consent before submitting.');
      return;
    }

    if (!this.patientConsentData) {
      this.toastr.error('Patient details not loaded. Please refresh and try again.');
      return;
    }

    if (Number(this.patientConsentData.termsAccepted || 0) === 1) {
      this.isSubmitted = true;
      this.toastr.info('Consent is already accepted.');
      return;
    }

    this.updateUserAffiliationConsent();
  }

  /**
   * UPDATE FLOW:
   * Complete update DTO -> encrypt -> generate NEW signature -> Update_External
   */
  private updateUserAffiliationConsent(): void {
    if (!this.patientConsentData) {
      this.toastr.error('Patient data missing.');
      return;
    }

    if (this.isSubmitting) return;

    const consentAcceptedOn = new Date().toISOString();
    const now = new Date();
    const data: any = this.patientConsentData;

    const rawPayload: HospitalUserAffiliationUpdateRequestDTO = {
      mappingId: Number(this.mappingId),
      userId: Number(data.userId || 0),
      hospitalId: Number(data.hospitalId || 0),
      affiliationType: data.affiliationType || 'Patient',
      designation: data.designation || 'Hospital Patient',
      joinedOn: data.joinedOn ? new Date(data.joinedOn) : now,
      department: data.department || '',
      specialization: data.specialization || '',
      education: data.education || '',
      isPrimary: Number(data.isPrimary || 0),
      employmentType: data.employmentType || '',
      consultationFee: Number(data.consultationFee || 0),
      yearsOfExperience: Number(data.yearsOfExperience || 0),
      registrationNumber: data.registrationNumber || '',
      licensingAuthority: data.licensingAuthority || '',
      yearOfRegistration: data.yearOfRegistration ? String(data.yearOfRegistration) : '',
      registrationDate: data.registrationDate ? new Date(data.registrationDate) : undefined,
      joiningDate: data.joiningDate ? new Date(data.joiningDate) : undefined,
      shiftType: data.shiftType || '',
      visitType: data.visitType || '',
      referredBy: data.referredBy || '',
      socialLink: data.socialLink || '',
      allergies: data.allergies || '',
      chronicCondition: data.chronicCondition || '',
      insuranceProvider: data.insuranceProvider || '',
      insurancePolicyNo: data.insurancePolicyNo || '',
      emiEligibilityStatus: data.emiEligibilityStatus || 'Under Verification',
      loanReferenceId: data.loanReferenceId || '',
      termsAccepted: 1,
      signatureUrl: data.signatureUrl || '',
      uploadDocumentUrl: data.uploadDocumentUrl || '',
      consultationUrl: data.consultationUrl || '',
      country: data.country || '',
      state: data.state || '',
      city: data.city || '',
      bloodGroup: data.bloodGroup || '',
      gender: data.gender || '',
      aadharNumber: data.aadharNumber || '',
      panNumber: data.panNumber || '',
      nationality: data.nationality || '',
      religion: data.religion || '',
      caste: data.caste || '',
      emergencyContactName: data.emergencyContactName || '',
      emergencyContactNo: data.emergencyContactNo || '',
      maritalStatus: data.maritalStatus || '',
      firstName: data.firstName || '',
      middleName: data.middleName || '',
      lastName: data.lastName || '',
      dob: data.dob ? new Date(data.dob) : undefined,
      mobileNo: data.mobileNo || '',
      emailId: data.emailId || '',
      profileImage: data.profileImage || '',
      address: data.address || '',
      pinCode: data.pinCode || '',
      otherDetail1: 'Consent Accepted',
      otherDetail2: consentAcceptedOn,
            otherDetail3: this.latitude !== null ? String(this.latitude) : '',
      otherDetail4: this.longitude !== null ? String(this.longitude) : '',
      otherDetail5: this.locationAccuracy !== null ? String(this.locationAccuracy) : '',
      otherDetail6: 'HOSPITAL_ADMISSION_CONSENT_V1',
      isActive: Number(data.isActive ?? 1),
      actionUser: 'PATIENT'
    };

    this.isSubmitting = true;

    try {
      const encryptedBody = this.createEncryptedExternalBody(rawPayload);

      // Generate a fresh signature for Update_External
      const signaturePayload: SignatureRequestDTO = {
        apiKey: environment.hospitalExternalApiKey,
        privateKeyPem: environment.privateKeyPem,
        method: 'POST',
        pathAndQuery: '/Update_Extrenal',
        body: JSON.stringify(encryptedBody)
      };

      this.userPermissionService.UserPermission_generateSignature(signaturePayload)
        .pipe(
          switchMap((signatureResponse: any) => {
            const signatureData = this.getValidatedSignatureResponse(signatureResponse);

            return this.hospitalUserAffiliationService.HospitalUserAffiliationUpdate_External(
              encryptedBody as unknown as HospitalUserAffiliationUpdateRequestDTO,
              signatureData.xApiKey,
              signatureData.xApiTimestamp,
              signatureData.xApiNonce,
              signatureData.xApiSignature
            );
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => {
            this.isSubmitting = false;
            this.isSubmitted = true;
            this.isAgreed = true;

            if (this.patientConsentData) {
              this.patientConsentData.termsAccepted = 1;
              this.patientConsentData.otherDetail1 = 'Consent Accepted';
              this.patientConsentData.otherDetail2 = consentAcceptedOn;
              this.patientConsentData.otherDetail3 = this.latitude !== null ? String(this.latitude) : '';
              this.patientConsentData.otherDetail4 = this.longitude !== null ? String(this.longitude) : '';
              this.patientConsentData.otherDetail5 = this.locationAccuracy !== null ? String(this.locationAccuracy) : '';
              this.patientConsentData.otherDetail6 = 'HOSPITAL_ADMISSION_CONSENT_V1';
            }

            this.toastr.success('Consent submitted successfully.');
          },
          error: (error: any) => {
            this.isSubmitting = false;
            console.error('Consent Update External failed:', error);
            const message = error?.error?.message || error?.error?.title || error?.message || 'Failed to submit consent.';
            this.toastr.error(message);
          }
        });
    } catch (error: any) {
      this.isSubmitting = false;
      console.error('Unable to prepare encrypted consent update:', error);
      this.toastr.error(error?.message || 'Unable to prepare consent update request.');
    }
  }

  /**
   * Converts the raw DTO into the encrypted external API body.
   */
  private createEncryptedExternalBody(rawPayload: unknown): HospitalUserAffiliationExternalEncryptedRequestDTO {
    const encryptedRequest: any = this.encryptionService.createEncryptedRequest(rawPayload);

    const encryptedBody: HospitalUserAffiliationExternalEncryptedRequestDTO = {
      EncData: encryptedRequest?.EncData || encryptedRequest?.encData || '',
      TokenKey: encryptedRequest?.TokenKey || encryptedRequest?.tokenKey || ''
    };

    if (!encryptedBody.EncData || !encryptedBody.TokenKey) {
      throw new Error('Encryption service did not return EncData and TokenKey.');
    }

    return encryptedBody;
  }

  private getValidatedSignatureResponse(response: any): SignatureResponseDTO {
    const signatureData = this.extractSignatureResponse(response);

    if (!signatureData.xApiKey || !signatureData.xApiTimestamp || !signatureData.xApiNonce || !signatureData.xApiSignature) {
      throw new Error('Invalid response received from Signature Generator.');
    }

    return signatureData;
  }

  private extractSignatureResponse(response: any): SignatureResponseDTO {
    const data = response?.data || response?.item || response?.result || response;

    return {
      xApiKey: data?.xApiKey || data?.XApiKey || '',
      xApiTimestamp: data?.xApiTimestamp || data?.XApiTimestamp || '',
      xApiNonce: data?.xApiNonce || data?.XApiNonce || '',
      xApiSignature: data?.xApiSignature || data?.XApiSignature || '',
      body: data?.body || data?.Body || ''
    };
  }

  private bindPatientConsentData(response: any): void {
    const data = response?.items?.[0] || response?.data?.items?.[0] || response?.data || response?.item || response;

    if (!data || !data.mappingId) {
      this.patientConsentData = null;
      this.toastr.error('Patient consent record not found.');
      return;
    }

    this.patientConsentData = data;

    if (Number(data.termsAccepted || 0) === 1) {
      this.isAgreed = true;
      this.isSubmitted = true;
    } else {
      this.isAgreed = false;
      this.isSubmitted = false;
    }
  }
}

